import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type {
  Company,
  CompaniesApiResponse,
  UseGetCompaniesReturn,
  UseGetCompaniesParams,
} from "../types"
import { PaginationInfo } from "src/components/tailwind-components/Pagination"

export const COMPANIES_LIST_URL = `${SMOOTH_LINE_API_URL}/base_api/smoother/companies`

/**
 * 会社一覧を取得するカスタムフック
 *
 * @param params - 取得パラメータ
 * @param params.page - ページ番号（デフォルト: 1）
 * @param params.per - 1ページあたりの件数（デフォルト: 25）
 * @param params.order - 並び順（asc | desc、デフォルト: asc）
 * @param params.search - 検索文字列（会社名で検索）
 * @returns 会社一覧とその操作メソッド
 */
export const useGetCompanies = (params: UseGetCompaniesParams = {}): UseGetCompaniesReturn => {
  const { getWithToken } = useSmoothApi()

  // URLにクエリパラメータを追加
  const searchParams = new URLSearchParams()

  if (params.page) {
    searchParams.append("page", params.page.toString())
  }
  if (params.per) {
    searchParams.append("per", params.per.toString())
  }
  if (params.order) {
    searchParams.append("order", params.order)
  }
  if (params.search) {
    searchParams.append("search", params.search)
  }

  const url = searchParams.toString()
    ? `${COMPANIES_LIST_URL}?${searchParams.toString()}`
    : COMPANIES_LIST_URL

  // SWRキーを生成（パラメータを含む）
  const swrKey = [url, params]

  /**
   * データ取得用のfetcher関数
   * @returns APIレスポンス
   */
  const fetcher = async (): Promise<CompaniesApiResponse> => {
    try {
      const response = await getWithToken(url)
      return response.data as CompaniesApiResponse
    } catch (error) {
      // エラーログ出力（開発環境での診断用）
      console.error("会社一覧の取得に失敗しました:", error)
      throw error
    }
  }

  // SWRでデータフェッチング
  const swr = useSWR(swrKey, fetcher, {
    // 再検証の設定
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    // エラー時の再試行設定
    errorRetryCount: 3,
    errorRetryInterval: 1000,
  })

  // データの抽出と型安全性の確保
  const companies: Company[] = swr.data?.companies || []

  // APIレスポンスの形式をPaginationInfo形式に変換
  const pagination: PaginationInfo | undefined = swr.data?.pagination
    ? {
        currentPage: swr.data.pagination.page,
        totalCount: swr.data.pagination.totalCount,
        totalPages: swr.data.pagination.totalPages,
      }
    : undefined

  const isLoading = !swr.data && !swr.error
  const error = swr.error
  const isEmpty = !isLoading && companies.length === 0

  /**
   * データを強制的にリフレッシュする関数
   * キャッシュをクリアして新しいデータを取得
   */
  const refresh = async () => {
    return swr.mutate(undefined, { revalidate: true })
  }

  return {
    // データ
    companies,
    pagination,
    data: swr.data,

    // 状態
    isLoading,
    error,
    isEmpty,

    // 操作メソッド
    mutate: swr.mutate,
    refresh,
  }
}
